import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import {
  ISidenavContainer,
  SIDENAV_CONTAINER,
} from '../sidenav-container.token';

export type SidenavMode = 'over' | 'side';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  host: {
    '[class]': 'classes',
    tabIndex: '-1',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input()
  set mode(value: SidenavMode) {
    if (value !== this._mode) {
      this._mode = value;
      this._container?.updateLayout();
    }
  }
  get mode(): SidenavMode {
    return this._mode;
  }

  @Input()
  set opened(value: boolean) {
    const coerced = coerceBooleanProperty(value);
    if (coerced !== this._opened) {
      if (coerced) {
        this.open();
      } else {
        this.close();
      }
    }
  }
  get opened(): boolean {
    return this._opened;
  }

  @Input() disableClose = false;

  @Output() openedChange = new EventEmitter<boolean>();
  @Output() closedStart = new EventEmitter<void>();

  readonly elementRef: ElementRef<HTMLElement>;

  private _mode: SidenavMode = 'over';
  private _opened = false;
  private _container: ISidenavContainer | null;

  constructor(
    elementRef: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef,
    @Optional() @Inject(SIDENAV_CONTAINER) container: ISidenavContainer | null
  ) {
    this.elementRef = elementRef;
    this._container = container;
  }

  ngOnInit(): void {
    this._container?.registerSidenav(this);
  }

  ngOnDestroy(): void {
    this._container?.deregisterSidenav(this);
  }

  get classes(): string {
    const classList: string[] = ['sidenav'];

    if (this._opened) {
      classList.push('sidenav--open');
    }

    classList.push(`sidenav--mode-${this._mode}`);

    return classList.join(' ');
  }

  open(): void {
    if (!this._opened) {
      this._opened = true;
      this._cdr.markForCheck();
      this.openedChange.emit(true);
      this._container?.updateLayout();
    }
  }

  close(): void {
    if (this._opened) {
      this.closedStart.emit();
      this._opened = false;
      this._cdr.markForCheck();
      this.openedChange.emit(false);
      this._container?.updateLayout();
    }
  }

  toggle(): void {
    if (this._opened) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Used by container to measure width for side mode margin. */
  _getWidth(): number {
    return this.elementRef.nativeElement.offsetWidth;
  }
}
